import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  Card,
  Spin,
  Alert,
  Typography,
  Space,
  Button,
  Tag,
  Statistic,
  Row,
  Col,
  DatePicker,
  Select,
  Collapse,
  message,
  Tooltip,
} from "antd";
import {
  StarOutlined,
  StarFilled,
  FileExcelOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { AgGridReact } from "ag-grid-react";
import {
  getUpcomingInvoices,
  setUpcomingPaymentPriority,
} from "../../api/accounting";
import dayjs from "dayjs";
import { convertToUSD } from "../../utils/currencyUtils";
import {
  getCachedData,
  setCachedData,
  CacheTTL,
} from "../../context/AccountingDataCache";
import useDisplayCurrency from "../../../../hooks/useDisplayCurrency";
import "./UpcomingPayments.css";
import { exportUpcomingVendorExcel } from "../../utils/exportUpcomingVendorExcel";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const UpcomingInvoices = ({ firstBrand = null, selectedBrands = [] }) => {
  const {
    refreshKey: currencyRefreshKey,
    displayCurrency,
    exchangeRate,
    isConverted,
  } = useDisplayCurrency();

  const [error, setError] = useState(null);
  const [priorityItems, setPriorityItems] = useState(new Set());
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(1, "month").startOf("month"),
    dayjs().endOf("month"),
  ]);
  const [activeKeys, setActiveKeys] = useState([]);
  const [selectedVendors, setSelectedVendors] = useState([]);

  const fetchingRef = useRef(false);
  const lastFetchedCacheKeyRef = useRef(null);

  const brandsToUse = useMemo(() => {
    if (selectedBrands && selectedBrands.length > 0) return selectedBrands;
    return firstBrand ? [firstBrand] : [];
  }, [selectedBrands, firstBrand]);

  const brandsKey = useMemo(() => {
    if (!brandsToUse || brandsToUse.length === 0) return "";
    return brandsToUse.map((b) => b.id).sort().join(",");
  }, [brandsToUse]);

  const dateRangeKey = useMemo(() => {
    if (!dateRange || dateRange.length !== 2) return "";
    return `${dateRange[0]?.format("YYYY-MM-DD") || ""}_${dateRange[1]?.format("YYYY-MM-DD") || ""}`;
  }, [dateRange]);

  const cacheKey = useMemo(() => {
    if (!brandsKey) return null;
    return `upcoming-invoices-${brandsKey}-${dateRangeKey}`;
  }, [brandsKey, dateRangeKey]);

  const initialInvoicesData = useMemo(() => {
    if (!cacheKey) return [];
    return getCachedData(cacheKey) || [];
  }, [cacheKey]);

  const [loading, setLoading] = useState(initialInvoicesData.length === 0);
  const [invoicesData, setInvoicesData] = useState(initialInvoicesData);

  const extractInvoiceId = useCallback((compositeId) => {
    const idStr = String(compositeId);
    if (idStr.includes("_")) return parseInt(idStr.split("_")[0]);
    return parseInt(compositeId);
  }, []);

  const filteredInvoicesData = useMemo(() => {
    if (!selectedVendors || selectedVendors.length === 0) return invoicesData;
    return invoicesData.filter((i) => selectedVendors.includes(i.vendor_name));
  }, [invoicesData, selectedVendors]);

  const handlePriorityToggle = useCallback(
    async (rowData) => {
      const compositeId = String(rowData.id);
      const currentPriority = priorityItems.has(compositeId);
      const newPriority = !currentPriority;

      setPriorityItems((prev) => {
        const newSet = new Set(prev);
        newPriority ? newSet.add(compositeId) : newSet.delete(compositeId);
        return newSet;
      });

      const updatedData = invoicesData.map((inv) =>
        String(inv.id) === compositeId ? { ...inv, is_priority: newPriority } : inv
      );
      setInvoicesData(updatedData);

      try {
        const brandId = rowData.brand_id || brandsToUse[0]?.id;
        const realInvoiceId = extractInvoiceId(compositeId);
        if (brandId && realInvoiceId) {
          await setUpcomingPaymentPriority(realInvoiceId, brandId, newPriority, compositeId);
        }
      } catch {
        setPriorityItems((prev) => {
          const newSet = new Set(prev);
          newPriority ? newSet.delete(compositeId) : newSet.add(compositeId);
          return newSet;
        });
        const revertedData = invoicesData.map((inv) =>
          String(inv.id) === compositeId ? { ...inv, is_priority: !newPriority } : inv
        );
        setInvoicesData(revertedData);
      }
    },
    [priorityItems, invoicesData, brandsToUse, extractInvoiceId]
  );

  const handleVendorPriorityToggle = useCallback(
    async (vendorName) => {
      const vendorInvoices = filteredInvoicesData.filter((i) => (i.vendor_name || "Unknown") === vendorName);
      if (vendorInvoices.length === 0) return;

      const allArePriority = vendorInvoices.every((i) => priorityItems.has(String(i.id)) || i.is_priority);
      const newPriority = !allArePriority;

      const ids = vendorInvoices.map((i) => String(i.id));
      setPriorityItems((prev) => {
        const newSet = new Set(prev);
        ids.forEach((id) => (newPriority ? newSet.add(id) : newSet.delete(id)));
        return newSet;
      });
      setInvoicesData((prev) =>
        prev.map((inv) => (ids.includes(String(inv.id)) ? { ...inv, is_priority: newPriority } : inv))
      );

      const failed = [];
      for (const inv of vendorInvoices) {
        try {
          const brandId = inv.brand_id || brandsToUse[0]?.id;
          const realId = extractInvoiceId(String(inv.id));
          if (brandId && realId) {
            await setUpcomingPaymentPriority(realId, brandId, newPriority, String(inv.id));
          }
        } catch {
          failed.push(String(inv.id));
        }
      }
      if (failed.length > 0) {
        message.warning(`${failed.length} item(s) failed to update priority`);
        setPriorityItems((prev) => {
          const newSet = new Set(prev);
          failed.forEach((id) => (newPriority ? newSet.delete(id) : newSet.add(id)));
          return newSet;
        });
        setInvoicesData((prev) =>
          prev.map((inv) => (failed.includes(String(inv.id)) ? { ...inv, is_priority: !newPriority } : inv))
        );
      }
    },
    [filteredInvoicesData, priorityItems, brandsToUse, extractInvoiceId]
  );

  const fetchUpcomingInvoices = useCallback(
    async (force = false) => {
      if (!brandsToUse || brandsToUse.length === 0 || !cacheKey) {
        setError("Please select a brand");
        setInvoicesData([]);
        return;
      }

      if (!force) {
        const cachedData = getCachedData(cacheKey);
        if (cachedData) {
          setInvoicesData(cachedData);
          const priorityIds = new Set(cachedData.filter((i) => i.is_priority).map((i) => String(i.id)));
          setPriorityItems(priorityIds);
          setLoading(false);
          lastFetchedCacheKeyRef.current = cacheKey;
          return;
        }
      }

      if (!force && invoicesData.length > 0 && lastFetchedCacheKeyRef.current === cacheKey) {
        setLoading(false);
        return;
      }

      if (fetchingRef.current && !force) return;
      fetchingRef.current = true;
      setLoading(true);
      setError(null);

      try {
        const brands = brandsToUse.map((b) => b.id);
        const startDate = dateRange?.[0]?.startOf("day") || dayjs().startOf("day");
        const endDate = dateRange?.[1]?.startOf("day") || dayjs().add(5, "day");

        const data = await getUpcomingInvoices(brands, {
          upcoming_only: true,
          invoice_type: "invoice",
          start_date: startDate.format("YYYY-MM-DD"),
          end_date: endDate.format("YYYY-MM-DD"),
        });

        if (!data || !data.upcoming_invoices) {
          setInvoicesData([]);
          setPriorityItems(new Set());
          return;
        }

        const invoices = data.upcoming_invoices || [];
        const priorityIds = new Set(invoices.filter((i) => i.is_priority).map((i) => String(i.id)));
        setPriorityItems(priorityIds);
        setInvoicesData(invoices);
        setCachedData(cacheKey, invoices, CacheTTL.MEDIUM);
        lastFetchedCacheKeyRef.current = cacheKey;
      } catch (err) {
        setError(`Failed to load upcoming invoices: ${err.response?.data?.error || err.message}`);
        setInvoicesData([]);
        setPriorityItems(new Set());
      } finally {
        setLoading(false);
        fetchingRef.current = false;
      }
    },
    [brandsToUse, dateRange, cacheKey, invoicesData.length]
  );

  useEffect(() => {
    if (!brandsKey || !cacheKey) {
      setInvoicesData([]);
      setPriorityItems(new Set());
      return;
    }
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      setInvoicesData(cachedData);
      const priorityIds = new Set(cachedData.filter((i) => i.is_priority).map((i) => String(i.id)));
      setPriorityItems(priorityIds);
      setLoading(false);
      lastFetchedCacheKeyRef.current = cacheKey;
      return;
    }
    if (invoicesData.length === 0 || lastFetchedCacheKeyRef.current !== cacheKey) {
      fetchUpcomingInvoices();
    }
  }, [cacheKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const vendorOptions = useMemo(() => {
    const vendors = [...new Set(invoicesData.map((i) => i.vendor_name).filter(Boolean))].sort();
    return vendors.map((v) => ({ label: v, value: v }));
  }, [invoicesData]);

  const vendorGroups = useMemo(() => {
    const groups = {};
    filteredInvoicesData.forEach((inv) => {
      const vName = inv.vendor_name || "Unknown";
      if (!groups[vName]) {
        groups[vName] = { vendor_name: vName, invoices: [], totalAmount: 0 };
      }
      groups[vName].invoices.push(inv);
      groups[vName].totalAmount += convertToUSD(inv.amount || 0, inv.currency || "$");
    });
    return Object.values(groups).sort((a, b) => a.vendor_name.localeCompare(b.vendor_name));
  }, [filteredInvoicesData]);

  useEffect(() => {
    setActiveKeys(vendorGroups.map((g) => g.vendor_name));
  }, [vendorGroups]);

  const totals = useMemo(() => {
    const count = filteredInvoicesData.length;
    const totalAmount = filteredInvoicesData.reduce((sum, inv) => {
      return sum + convertToUSD(inv.amount || 0, inv.currency || "$");
    }, 0);
    return { count, totalAmount };
  }, [filteredInvoicesData]);

  const fmtAmount = useCallback(
    (val) => {
      const display = isConverted ? val * exchangeRate : val;
      return display.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    [isConverted, exchangeRate]
  );

  const currencyPrefix = isConverted ? displayCurrency : "$";

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      suppressMovable: false,
    }),
    []
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Priority",
        field: "is_priority",
        width: 90,
        maxWidth: 90,
        sortable: true,
        filter: false,
        cellRenderer: (params) => {
          const record = params.data;
          if (!record) return null;

          if (record._isTotal) {
            const vendorName = String(record.id).replace("total-", "");
            const vendorInvoices = filteredInvoicesData.filter((i) => (i.vendor_name || "Unknown") === vendorName);
            const allPriority = vendorInvoices.length > 0 && vendorInvoices.every((i) => priorityItems.has(String(i.id)) || i.is_priority);
            return (
              <Tooltip title={allPriority ? "Remove priority from all" : "Mark all as priority"}>
                <Button
                  type={allPriority ? "primary" : "default"}
                  size="small"
                  icon={allPriority ? <StarFilled /> : <StarOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleVendorPriorityToggle(vendorName);
                  }}
                  style={{
                    backgroundColor: allPriority ? "#faad14" : undefined,
                    borderColor: allPriority ? "#faad14" : undefined,
                  }}
                />
              </Tooltip>
            );
          }

          const compositeId = String(record.id);
          const isPriority = priorityItems.has(compositeId) || record.is_priority;
          return (
            <Button
              type={isPriority ? "primary" : "default"}
              size="small"
              icon={isPriority ? <StarFilled /> : <StarOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handlePriorityToggle(record);
              }}
              style={{
                backgroundColor: isPriority ? "#faad14" : undefined,
                borderColor: isPriority ? "#faad14" : undefined,
              }}
            />
          );
        },
      },
      {
        headerName: "Client",
        field: "client_name",
        minWidth: 150,
        flex: 1,
      },
      {
        headerName: "Brand",
        field: "brand_name",
        minWidth: 120,
        flex: 1,
      },
      {
        headerName: "Invoice Date",
        field: "invoice_date",
        width: 120,
        valueFormatter: (params) =>
          params.value ? dayjs(params.value).format("DD-MM-YYYY") : "-",
      },
      {
        headerName: "Invoice Number",
        field: "invoice_number",
        width: 120,
      },
      {
        headerName: "Payment Terms",
        field: "payment_terms_text",
        minWidth: 160,
        flex: 1,
        valueGetter: (params) =>
          params.data?.payment_terms_text || params.data?.description || "-",
      },
      {
        headerName: "Percentage",
        field: "percentage",
        width: 110,
        type: "numericColumn",
      },
      {
        headerName: "Due Date",
        field: "due_date",
        width: 120,
        valueFormatter: (params) =>
          params.value ? dayjs(params.value).format("DD-MM-YYYY") : "-",
      },
      {
        headerName: "Due In",
        field: "due_in_text",
        width: 140,
        cellRenderer: (params) => {
          const record = params.data;
          if (!record) return "-";
          if (record.is_overdue) {
            return <Tag color="red">{params.value || "OVERDUE"}</Tag>;
          }
          return <span style={{ color: "#52c41a" }}>{params.value || "-"}</span>;
        },
      },
      {
        headerName: "Total Amount Due",
        field: "amount",
        width: 160,
        type: "numericColumn",
        valueGetter: (params) => {
          if (!params.data) return 0;
          return convertToUSD(params.data.amount || 0, params.data.currency || "$");
        },
        valueFormatter: (params) => `${currencyPrefix} ${fmtAmount(params.value || 0)}`,
        cellStyle: (params) => {
          if (params.data?.is_overdue) return { color: "#ff4d4f", fontWeight: 600 };
          return { fontWeight: 500 };
        },
      },
    ],
    [priorityItems, handlePriorityToggle, handleVendorPriorityToggle, filteredInvoicesData, currencyPrefix, fmtAmount]
  );

  const getRowStyle = useCallback(
    (params) => {
      if (!params.data) return {};
      const isPriority = priorityItems.has(String(params.data.id)) || params.data.is_priority;
      if (isPriority && params.data.is_overdue) return { background: "#ffa39e" };
      if (params.data.is_overdue) return { background: "#fff2f0" };
      if (isPriority) return { background: "#e6f7ff" };
      return {};
    },
    [priorityItems]
  );

  const invoiceExportHeaders = [
    "Vendor",
    "Client",
    "Brand",
    "Invoice Date",
    "Invoice Number",
    "Payment Terms",
    "Percentage",
    "Due Date",
    "Due In",
    "Total Amount Due (USD)",
  ];

  const mapInvoiceToExportCells = (inv) => [
    inv.vendor_name,
    inv.client_name || "",
    inv.brand_name || "",
    inv.invoice_date ? dayjs(inv.invoice_date).format("DD-MM-YYYY") : "",
    inv.invoice_number,
    inv.payment_terms_text || inv.description || "",
    inv.percentage ?? "",
    inv.due_date ? dayjs(inv.due_date).format("DD-MM-YYYY") : "",
    inv.due_in_text || "",
    Number(convertToUSD(inv.amount || 0, inv.currency || "$").toFixed(2)),
  ];

  const handleExportCSV = () => {
    if (filteredInvoicesData.length === 0) {
      message.warning("No data to export");
      return;
    }

    const dateLabel = dateRange ? `${dateRange[0]?.format("YYYY-MM-DD")} to ${dateRange[1]?.format("YYYY-MM-DD")}` : "";
    const vendorLabel = selectedVendors.length > 0 ? selectedVendors.join(", ") : "All Vendors";
    const rows = [];

    vendorGroups.forEach((group) => {
      rows.push([`=== Vendor: ${group.vendor_name} ===`, "", "", "", "", "", "", "", "", ""]);
      rows.push(invoiceExportHeaders);
      group.invoices.forEach((inv) => {
        rows.push([
          inv.vendor_name,
          inv.client_name || "",
          inv.brand_name || "",
          inv.invoice_date ? dayjs(inv.invoice_date).format("DD-MM-YYYY") : "",
          inv.invoice_number,
          inv.payment_terms_text || inv.description || "",
          inv.percentage ?? "",
          inv.due_date ? dayjs(inv.due_date).format("DD-MM-YYYY") : "",
          inv.due_in_text || "",
          convertToUSD(inv.amount || 0, inv.currency || "$").toFixed(2),
        ]);
      });
      rows.push(["Total", "", "", "", "", "", "", "", "", group.totalAmount.toFixed(2)]);
      rows.push([]);
    });

    const csvContent = [
      `Payment Schedule - Invoices (by vendor),,,,,,,,,`,
      `Date Range: ${dateLabel},,,,,,,,,`,
      `Vendors: ${vendorLabel},,,,,,,,,`,
      ``,
      ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Upcoming_Invoices_${dayjs().format("YYYY-MM-DD")}.csv`;
    link.click();
    message.success("Exported successfully!");
  };

  const handleExportExcel = async () => {
    if (filteredInvoicesData.length === 0) {
      message.warning("No data to export");
      return;
    }
    const dateLabel = dateRange
      ? `${dateRange[0]?.format("YYYY-MM-DD")} to ${dateRange[1]?.format("YYYY-MM-DD")}`
      : "";
    const vendorLabel = selectedVendors.length > 0 ? selectedVendors.join(", ") : "All Vendors";
    try {
      await exportUpcomingVendorExcel({
        filename: `Upcoming_Invoices_${dayjs().format("YYYY-MM-DD")}`,
        reportTitle: "Payment Schedule — Invoices (by vendor)",
        dateRangeLabel: dateLabel,
        vendorFilterLabel: vendorLabel,
        vendorSections: vendorGroups.map((g) => ({
          vendor_name: g.vendor_name,
          rows: g.invoices,
          totalAmount: g.totalAmount,
        })),
        headers: invoiceExportHeaders,
        mapRowToCells: mapInvoiceToExportCells,
      });
      message.success("Excel exported: one sheet with a table per vendor and grand total.");
    } catch (e) {
      console.error(e);
      message.error(e?.message || "Excel export failed");
    }
  };

  const priorityFilteredData = useMemo(() => {
    return filteredInvoicesData.filter((i) => priorityItems.has(String(i.id)) || i.is_priority);
  }, [filteredInvoicesData, priorityItems]);

  const handleExportPriorityCSV = () => {
    if (priorityFilteredData.length === 0) {
      message.warning("No priority items to export");
      return;
    }

    const dateLabel = dateRange ? `${dateRange[0]?.format("YYYY-MM-DD")} to ${dateRange[1]?.format("YYYY-MM-DD")}` : "";
    const vendorLabel = selectedVendors.length > 0 ? selectedVendors.join(", ") : "All Vendors";
    const rows = [];

    const groups = {};
    priorityFilteredData.forEach((inv) => {
      const vName = inv.vendor_name || "Unknown";
      if (!groups[vName]) groups[vName] = { vendor_name: vName, invoices: [], totalAmount: 0 };
      groups[vName].invoices.push(inv);
      groups[vName].totalAmount += convertToUSD(inv.amount || 0, inv.currency || "$");
    });
    const priorityGroups = Object.values(groups).sort((a, b) => a.vendor_name.localeCompare(b.vendor_name));

    priorityGroups.forEach((group) => {
      rows.push([`=== Vendor: ${group.vendor_name} (priority) ===`, "", "", "", "", "", "", "", "", ""]);
      rows.push(invoiceExportHeaders);
      group.invoices.forEach((inv) => {
        rows.push([
          inv.vendor_name,
          inv.client_name || "",
          inv.brand_name || "",
          inv.invoice_date ? dayjs(inv.invoice_date).format("DD-MM-YYYY") : "",
          inv.invoice_number,
          inv.payment_terms_text || inv.description || "",
          inv.percentage ?? "",
          inv.due_date ? dayjs(inv.due_date).format("DD-MM-YYYY") : "",
          inv.due_in_text || "",
          convertToUSD(inv.amount || 0, inv.currency || "$").toFixed(2),
        ]);
      });
      rows.push(["Total", "", "", "", "", "", "", "", "", group.totalAmount.toFixed(2)]);
      rows.push([]);
    });

    const csvContent = [
      `Priority Payment Schedule - Invoices (by vendor),,,,,,,,,`,
      `Date Range: ${dateLabel},,,,,,,,,`,
      `Vendors: ${vendorLabel},,,,,,,,,`,
      ``,
      ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Priority_Upcoming_Invoices_${dayjs().format("YYYY-MM-DD")}.csv`;
    link.click();
    message.success("Priority items exported successfully!");
  };

  const handleExportPriorityExcel = async () => {
    if (priorityFilteredData.length === 0) {
      message.warning("No priority items to export");
      return;
    }
    const groups = {};
    priorityFilteredData.forEach((inv) => {
      const vName = inv.vendor_name || "Unknown";
      if (!groups[vName]) groups[vName] = { vendor_name: vName, invoices: [], totalAmount: 0 };
      groups[vName].invoices.push(inv);
      groups[vName].totalAmount += convertToUSD(inv.amount || 0, inv.currency || "$");
    });
    const priorityGroups = Object.values(groups).sort((a, b) => a.vendor_name.localeCompare(b.vendor_name));
    const dateLabel = dateRange
      ? `${dateRange[0]?.format("YYYY-MM-DD")} to ${dateRange[1]?.format("YYYY-MM-DD")}`
      : "";
    const vendorLabel = selectedVendors.length > 0 ? selectedVendors.join(", ") : "All Vendors";
    try {
      await exportUpcomingVendorExcel({
        filename: `Priority_Upcoming_Invoices_${dayjs().format("YYYY-MM-DD")}`,
        reportTitle: "Priority — Invoices (by vendor)",
        dateRangeLabel: dateLabel,
        vendorFilterLabel: vendorLabel,
        vendorSections: priorityGroups.map((g) => ({
          vendor_name: g.vendor_name,
          rows: g.invoices,
          totalAmount: g.totalAmount,
        })),
        headers: invoiceExportHeaders,
        mapRowToCells: mapInvoiceToExportCells,
      });
      message.success("Priority Excel exported.");
    } catch (e) {
      console.error(e);
      message.error(e?.message || "Excel export failed");
    }
  };

  const collapseItems = useMemo(
    () =>
      vendorGroups.map((group) => {
        const gridHeight = Math.min(Math.max(group.invoices.length * 40 + 100, 200), 600);
        return {
          key: group.vendor_name,
          label: (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
              <Text strong style={{ fontSize: 14 }}>{group.vendor_name}</Text>
              <Space size="large">
                <Tag>{group.invoices.length} invoice{group.invoices.length !== 1 ? "s" : ""}</Tag>
                <Text strong style={{ fontSize: 14 }}>
                  {currencyPrefix} {fmtAmount(group.totalAmount)}
                </Text>
              </Space>
            </div>
          ),
          children: (
            <div className="ag-theme-alpine" style={{ height: gridHeight, width: "100%" }}>
              <AgGridReact
                rowData={group.invoices}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                getRowId={(params) => String(params.data.id)}
                getRowStyle={getRowStyle}
                animateRows={false}
                suppressScrollOnNewData={true}
                domLayout="normal"
                headerHeight={36}
                rowHeight={40}
                enableCellTextSelection={true}
                ensureDomOrder={true}
                pinnedBottomRowData={[
                  {
                    id: `total-${group.vendor_name}`,
                    client_name: "Total",
                    brand_name: "",
                    invoice_date: null,
                    invoice_number: "",
                    payment_terms_text: "",
                    percentage: "",
                    due_date: null,
                    due_in_text: "",
                    amount: group.totalAmount,
                    currency: "$",
                    _isTotal: true,
                  },
                ]}
              />
            </div>
          ),
        };
      }),
    [vendorGroups, columnDefs, defaultColDef, getRowStyle, currencyPrefix, fmtAmount]
  );

  return (
    <Card className="upcoming-invoices-card">
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <Title level={4} style={{ margin: 0 }}>Payment Schedule — Invoices</Title>
          <Space wrap>
            <Text strong>Vendor:</Text>
            <Select
              mode="multiple"
              allowClear
              showSearch
              placeholder="All Vendors"
              value={selectedVendors}
              onChange={(val) => setSelectedVendors(val || [])}
              options={vendorOptions}
              style={{ minWidth: 250, maxWidth: 500 }}
              maxTagCount="responsive"
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
            />
            <Text strong>Date Range:</Text>
            <RangePicker
              value={dateRange}
              onChange={(dates) =>
                setDateRange(dates || [dayjs().subtract(1, "month").startOf("month"), dayjs().endOf("month")])
              }
              allowClear={false}
            />
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={handleExportExcel}
              disabled={filteredInvoicesData.length === 0}
            >
              Export Excel
            </Button>
            <Button
              icon={<FileExcelOutlined />}
              onClick={handleExportPriorityExcel}
              disabled={priorityFilteredData.length === 0}
              style={priorityFilteredData.length > 0 ? { borderColor: "#faad14", color: "#faad14" } : {}}
            >
              Priority Excel ({priorityFilteredData.length})
            </Button>
          </Space>
        </div>

        <Row gutter={16}>
          <Col span={12}>
            <Card size="small">
              <Statistic title="Total Invoices" value={totals.count} valueStyle={{ color: "#1890ff" }} />
            </Card>
          </Col>
          <Col span={12}>
            <Card size="small">
              <Statistic
                title="Total Amount"
                value={isConverted ? totals.totalAmount * exchangeRate : totals.totalAmount}
                prefix={
                  isConverted ? (
                    <Tooltip title={`Original: $${totals.totalAmount.toLocaleString("en-US", { minimumFractionDigits: 2 })} USD`}>
                      <span><SwapOutlined style={{ fontSize: 14, marginRight: 4 }} />{displayCurrency === "USD" ? "$" : ""}</span>
                    </Tooltip>
                  ) : "$"
                }
                suffix={isConverted ? displayCurrency : ""}
                precision={2}
                valueStyle={{ color: "#ff4d4f" }}
                formatter={(value) => value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              />
            </Card>
          </Col>
        </Row>

        {error && <Alert message={error} type="error" showIcon />}

        <Spin spinning={loading}>
          {vendorGroups.length === 0 && !loading ? (
            <div style={{ textAlign: "center", padding: 40, color: "#999" }}>
              No upcoming invoices found for the selected date range.
            </div>
          ) : (
            <Collapse
              activeKey={activeKeys}
              onChange={(keys) => setActiveKeys(keys)}
              items={collapseItems}
              style={{ background: "#fff" }}
            />
          )}
        </Spin>
      </Space>
    </Card>
  );
};

export default UpcomingInvoices;
